import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Button, HasPermissions, UserNameLink } from "components";
import { getFullName, permissionList, useGetUserUrl } from "helpers";
import {
	useConcurrenceClaimMutation,
	useConcurrenceUnclaimMutation,
} from "queries";
import { merchantInfoActions, uiActions } from "store";
import { getCurrentUserId } from "store/user/user.reducer";
import { UserType } from "types";

import styles from "./underwritingNavInfo.module.scss";

type AssigneeItemProps = {
	assignee: UserType;
	applicationId: string;
	isLoading: boolean;
	refetchApplicationInfo: Function;
};

const AssignItemConcurrence = ({
	assignee,
	applicationId,
	isLoading,
	refetchApplicationInfo,
}: AssigneeItemProps) => {
	const dispatch = useDispatch();
	const currentUserId = useSelector(getCurrentUserId);
	const { tab } = useParams<any>();

	const assigneeName = assignee ? getFullName(assignee) : "Unassigned";
	const assigneeUrl = useGetUserUrl(assignee);
	const isAssignToUser = currentUserId === assignee?.id;
	const isAppHasAssignee = assignee?.id;

	const {
		mutate: handleConcurrenceClaim,
		isLoading: isLoadingHandleConcurrenceClaim,
	} = useConcurrenceClaimMutation();

	const {
		mutate: handleConcurrenceUnclaim,
		isLoading: isLoadingHandleConcurrenceUnclaim,
	} = useConcurrenceUnclaimMutation();

	const onConcurrenceClaimClick = useCallback(() => {
		refetchApplicationInfo?.();
		if (isAssignToUser) {
			handleConcurrenceUnclaim?.({
				id: applicationId,
				onSuccessHandler: () => {
					dispatch(
						merchantInfoActions.getMerchantInformation(
							applicationId,
							tab,
						),
					);
				},
			});
		} else {
			if (isAppHasAssignee) {
				dispatch(
					uiActions.showModal("CONFIRMATION", {
						title: [
							`This is already assigned to ${assigneeName}. Are you sure you want to claim it?`,
						],
						onConfirm() {
							handleConcurrenceClaim?.({
								id: applicationId,
								onSuccessHandler: () => {
									dispatch(
										merchantInfoActions.getMerchantInformation(
											applicationId,
											tab,
										),
									);
								},
							});
						},
					}),
				);
			} else {
				handleConcurrenceClaim?.({
					id: applicationId,
					onSuccessHandler: () => {
						dispatch(
							merchantInfoActions.getMerchantInformation(
								applicationId,
								tab,
							),
						);
					},
				});
			}
		}
	}, [
		applicationId,
		assigneeName,
		dispatch,
		handleConcurrenceClaim,
		handleConcurrenceUnclaim,
		isAppHasAssignee,
		isAssignToUser,
		refetchApplicationInfo,
		tab,
	]);

	return (
		<div className={styles.assigneeHolder}>
			<UserNameLink
				fullName={assigneeName}
				url={assigneeUrl}
				isLoading={isLoading}
			/>

			<HasPermissions
				requiredPermissions={
					permissionList.UnderwritingSensitiveCreditInfo
				}
			>
				<Button
					className={styles.assigneeButton}
					minWidth="auto"
					variant="secondary"
					value={isAssignToUser ? "unclaim" : "claim"}
					onClick={onConcurrenceClaimClick}
					disabled={
						isLoadingHandleConcurrenceClaim ||
						isLoadingHandleConcurrenceUnclaim
					}
				/>
			</HasPermissions>
		</div>
	);
};

export default AssignItemConcurrence;
